import { enableProdMode, provideZonelessChangeDetection, ErrorHandler, APP_INITIALIZER, importProvidersFrom } from '@angular/core';

import { errorHandlerFactory } from './app/app.module';
import { environment } from './environments/environment';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LoadingInterceptor } from './app/loading/loading.interceptor';
import { AuthenticationInterceptor } from './app/authentication/authentication.interceptor';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { CookieService } from 'ngx-cookie-service';
import { Title, BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideScrollbarOptions, NgScrollbarModule } from 'ngx-scrollbar';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { loadExistingToken } from './app/authentication/store/authentication.action';
import { SharedModule } from './app/shared/shared.module';
import { AppRoutingModule } from './app/app-routing.module';
import { AuthenticationModule } from './app/authentication/authentication.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BuildingModule } from './app/building/building.module';
import { CockpitModule } from './app/cockpit/cockpit.module';
import { TranslocoRootModule } from './app/transloco/transloco-root.module';
import { EffectsModule } from '@ngrx/effects';
import { AdminEffects } from './app/admin/store/admin.effects';
import { AuthenticationEffects } from './app/authentication/store/authentication.effects';
import { BuildingEffects } from './app/building/store/building.effects';
import { BuildingsEffects } from './app/building/store/buildings.effects';
import { ControlEffects } from './app/control/store/control.effects';
import { MapEffects } from './app/map/store/map.effects';
import { DocumentEffects } from './app/building/store/document.effects';
import { LoadingEffects } from './app/loading/store/loading.effects';
import { ErrorReportingEffects } from './app/error-reporting/store/error-reporting.effects';
import { FeatureFlagsEffects } from './app/features/store/feature-flags.effects';
import { ReferenceDataEffects } from './app/building/store/reference-data.effects';
import { RouterEffects } from './app/router/store/router.effects';
import { TagEffects } from './app/building/store/tag.effects';
import { UserEffects } from './app/authentication/store/user.effects';
import { NotificationEffects } from './app/notification/store/notification.effects';
import { ControlDefectEffects } from './app/control/store/control-defect.effects';
import { MotdEffects } from './app/authentication/store/motd.effects';
import { BuildingCasesEffects } from './app/building/store/building-cases.effects';
import { BuildingTechnicalInstallationsEffects } from './app/building/store/building-technical-installations.effects';
import { EnvironmentConfigEffects } from './app/environment-config/store/environment-config.effects';
import { TechnicalInstallationEffects } from './app/building/store/technical-installation.effects';
import { AssessmentEffects } from './app/assessment/store/assessment.effects';
import { NavigationRailModule } from './app/navigation-rail/navigation-rail.module';
import { NetworkModule } from './app/network/network.module';
import { NgxIndexedDBModule, DBConfig } from 'ngx-indexed-db';
import { StoreModule } from '@ngrx/store';
import { reducers, metaReducers } from './app/reducers';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { SibatRouteSerializer } from './app/router/sibat-route-serializer';
import { AppComponent } from './app/app.component';

const dbConfig: DBConfig = {
  name: 'control',
  version: 1,
  objectStoresMeta: [
    {
      store: 'defects',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [],
    },
  ],
};

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    // Must run before AppRoutingModule's APP_INITIALIZER (which performs the enabledBlocking
    // initial navigation) so AuthenticationGuard sees a restored token on a hard refresh instead
    // of redirecting to /signin before the cookie has been read back into the store.
    {
      provide: APP_INITIALIZER,
      useFactory: (store: Store) => () => store.dispatch(loadExistingToken()),
      deps: [Store],
      multi: true,
    },
    importProvidersFrom(
      SharedModule,
      AppRoutingModule,
      AuthenticationModule,
      BrowserAnimationsModule,
      BrowserModule,
      BuildingModule,
      CockpitModule,
      TranslocoRootModule,
      EffectsModule.forRoot([
        AdminEffects,
        AuthenticationEffects,
        BuildingEffects,
        BuildingsEffects,
        ControlEffects,
        MapEffects,
        DocumentEffects,
        LoadingEffects,
        ErrorReportingEffects,
        FeatureFlagsEffects,
        ReferenceDataEffects,
        RouterEffects,
        TagEffects,
        UserEffects,
        NotificationEffects,
        ControlDefectEffects,
        MotdEffects,
        BuildingCasesEffects,
        BuildingTechnicalInstallationsEffects,
        EnvironmentConfigEffects,
        TechnicalInstallationEffects,
        AssessmentEffects,
      ]),
      NavigationRailModule,
      NetworkModule,
      NgScrollbarModule,
      NgxIndexedDBModule.forRoot(dbConfig),
      StoreModule.forRoot(reducers, {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
      }),
      StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
      StoreRouterConnectingModule.forRoot({
        serializer: SibatRouteSerializer,
      })
    ),
    provideZonelessChangeDetection(),
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthenticationInterceptor, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'fr-CH' },
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    CookieService,
    Title,
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    provideHttpClient(withInterceptorsFromDi()),
    provideScrollbarOptions({
      visibility: 'hover',
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: (transloco: TranslocoService) => () => firstValueFrom(transloco.load('de')),
      deps: [TranslocoService],
      multi: true,
    },
  ],
}).catch(err => console.error(err));
